import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Check, X, Trash2 } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
const fmtT = (d) => { const x = new Date(d); let h = x.getHours(); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${pad(x.getMinutes())} ${ap}`; };

// Admin approvals hub: time-off, open-shift claims, and shift swaps.
export function RequestsDialog({ open, onOpenChange, onChanged }) {
  const [timeOff, setTimeOff] = useState([]);
  const [claims, setClaims] = useState([]);
  const [swaps, setSwaps] = useState([]);

  const load = () => {
    staffApi.get('/scheduling/time-off?status=PENDING').then(({ data }) => setTimeOff(data.requests));
    staffApi.get('/scheduling/open-shift-claims?status=PENDING').then(({ data }) => setClaims(data.claims));
    staffApi.get('/scheduling/swaps').then(({ data }) => setSwaps(data.requests));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const act = (fn) => async () => {
    try { await fn(); toast.success('Done'); load(); onChanged?.(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="requests-dialog" className="max-h-[85vh] overflow-y-auto max-w-lg">
        <DialogHeader><DialogTitle>Scheduling Requests</DialogTitle></DialogHeader>
        <Tabs defaultValue="timeoff">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="timeoff" data-testid="req-tab-timeoff">Time Off ({timeOff.length})</TabsTrigger>
            <TabsTrigger value="claims" data-testid="req-tab-claims">Claims ({claims.length})</TabsTrigger>
            <TabsTrigger value="swaps" data-testid="req-tab-swaps">Swaps ({swaps.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="timeoff" className="space-y-2 mt-3">
            {timeOff.length === 0 && <p className="text-sm text-muted-ink py-3">No pending time-off requests.</p>}
            {timeOff.map((r) => (
              <div key={r.id} className="border border-border/40 rounded-lg p-3" data-testid="timeoff-request-row">
                <p className="text-sm font-medium text-charcoal">{r.staffName} · {r.type}</p>
                <p className="text-xs text-muted-ink">{fmtDate(r.startDate)} – {fmtDate(r.endDate)}{r.reason ? ` · ${r.reason}` : ''}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" data-testid="timeoff-approve-btn" onClick={act(() => staffApi.post(`/scheduling/time-off/${r.id}/review`, { decision: 'APPROVED' }))} className="bg-coriander hover:bg-coriander/90 gap-1"><Check size={14} /> Approve</Button>
                  <Button size="sm" variant="outline" data-testid="timeoff-reject-btn" onClick={act(() => staffApi.post(`/scheduling/time-off/${r.id}/review`, { decision: 'REJECTED' }))} className="text-destructive gap-1"><X size={14} /> Reject</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="claims" className="space-y-2 mt-3">
            {claims.length === 0 && <p className="text-sm text-muted-ink py-3">No pending open-shift claims.</p>}
            {claims.map((c) => (
              <div key={c.id} className="border border-border/40 rounded-lg p-3" data-testid="claim-request-row">
                <p className="text-sm font-medium text-charcoal">{c.staffName} wants an open shift</p>
                {c.shift && <p className="text-xs text-muted-ink">{fmtDate(c.shift.startAt)} · {fmtT(c.shift.startAt)}–{fmtT(c.shift.endAt)}{c.shift.position ? ` · ${c.shift.position.name}` : ''}</p>}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" data-testid="claim-approve-btn" onClick={act(() => staffApi.post(`/scheduling/open-shift-claims/${c.id}/review`, { decision: 'APPROVED' }))} className="bg-coriander hover:bg-coriander/90 gap-1"><Check size={14} /> Approve</Button>
                  <Button size="sm" variant="outline" data-testid="claim-reject-btn" onClick={act(() => staffApi.post(`/scheduling/open-shift-claims/${c.id}/review`, { decision: 'REJECTED' }))} className="text-destructive gap-1"><X size={14} /> Reject</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="swaps" className="space-y-2 mt-3">
            {swaps.length === 0 && <p className="text-sm text-muted-ink py-3">No swap/replacement requests.</p>}
            {swaps.map((s) => (
              <div key={s.id} className="border border-border/40 rounded-lg p-3" data-testid="swap-request-row">
                <p className="text-sm font-medium text-charcoal">{s.type === 'SWAP' ? 'Shift swap' : 'Replacement'} · <span className="uppercase text-xs">{s.status}</span></p>
                {s.shift && <p className="text-xs text-muted-ink">{fmtDate(s.shift.startAt)} · {fmtT(s.shift.startAt)}–{fmtT(s.shift.endAt)}</p>}
                {s.status === 'ACCEPTED' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" data-testid="swap-approve-btn" onClick={act(() => staffApi.post(`/scheduling/swaps/${s.id}/review`, { decision: 'APPROVED' }))} className="bg-coriander hover:bg-coriander/90 gap-1"><Check size={14} /> Approve</Button>
                    <Button size="sm" variant="outline" data-testid="swap-reject-btn" onClick={act(() => staffApi.post(`/scheduling/swaps/${s.id}/review`, { decision: 'REJECTED' }))} className="text-destructive gap-1"><X size={14} /> Reject</Button>
                  </div>
                )}
                {s.status === 'PENDING' && <p className="text-xs text-amber-600 mt-1">Waiting for an employee to accept</p>}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesDialog({ open, onOpenChange, weekStart, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => staffApi.get('/scheduling/templates').then(({ data }) => setTemplates(data.templates));
  useEffect(() => { if (open) load(); }, [open]);

  const saveWeek = async () => {
    if (!name.trim()) return toast.error('Name your template');
    setBusy(true);
    try {
      await staffApi.post('/scheduling/templates/save-week', { name: name.trim(), weekStart: weekStart.toISOString() });
      toast.success('Week saved as template');
      setName('');
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const apply = async (t) => {
    if (!window.confirm(`Apply "${t.name}" (${t.itemCount} shifts) into the week starting ${weekStart.toLocaleDateString('en-CA')} as drafts?`)) return;
    try {
      const { data } = await staffApi.post(`/scheduling/templates/${t.id}/apply`, { weekStart: weekStart.toISOString() });
      toast.success(`Added ${data.created} draft shift(s)`);
      onApplied?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (t) => {
    try { await staffApi.delete(`/scheduling/templates/${t.id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="templates-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Schedule Templates</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label>Save current week as…</Label>
            <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Week" />
          </div>
          <Button data-testid="template-save-week-btn" disabled={busy} onClick={saveWeek} className="bg-saffron hover:bg-saffron-hover">Save Week</Button>
        </div>
        <div className="divide-y divide-border/30 mt-2">
          {templates.map((t) => (
            <div key={t.id} className="py-2.5 flex items-center justify-between" data-testid="template-row">
              <div>
                <p className="text-sm font-medium text-charcoal">{t.name}</p>
                <p className="text-xs text-muted-ink">{t.itemCount} shifts</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" data-testid="template-apply-btn" onClick={() => apply(t)} className="bg-coriander hover:bg-coriander/90">Load into week</Button>
                <Button size="sm" variant="ghost" data-testid="template-delete-btn" onClick={() => del(t)} className="text-destructive"><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
          {templates.length === 0 && <p className="text-sm text-muted-ink py-2">No templates yet. Build a week, then save it here.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
